import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axiosInstance from "../../api/axiosInstance";
import "../../styles/import-test-cases-modal.css";

const ACCEPTED_EXTENSIONS = [".xlsx", ".xls"];
const ACCEPT_ATTR = [
  ...ACCEPTED_EXTENSIONS,
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
].join(",");
const TEMPLATE_URL =
  "https://dtp-robitest.s3.amazonaws.com/develop/manual_templates/import_template_5ff4d8.xlsx";

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const hasAcceptedExtension = (fileName = "") => {
  const lower = fileName.toLowerCase();
  return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
};

const extractErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data) return error?.message || "Import failed. Please try again.";
  if (typeof data === "string") return data;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return data.message || data.error || "Import failed. Please try again.";
};

/**
 * Dropdown that renders its menu inside the modal (no native popup),
 * so long option labels are truncated instead of overflowing the dialog.
 */
const EnvironmentSelect = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const searchRef = useRef(null);
  const showSearch = options.length > 6;

  const filtered = query.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.trim().toLowerCase())
      )
    : options;
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setQuery("");
      return;
    }
    const idx = filtered.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
    if (showSearch) searchRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const el = listRef.current?.children?.[activeIndex];
    el?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex, isOpen]);

  const choose = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (!isOpen) {
      if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation(); // close the menu, not the modal
      setIsOpen(false);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) choose(filtered[activeIndex]);
    } else if (e.key === "Tab") {
      setIsOpen(false);
    }
  };

  return (
    <div className="itc-dd" ref={wrapRef} onKeyDown={handleKeyDown}>
      <button
        type="button"
        id="itc-env"
        className={`itc-dd-trigger ${isOpen ? "is-open" : ""}`}
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selected?.label || ""}
      >
        <span className={`itc-dd-value ${selected ? "" : "is-placeholder"}`}>
          {selected?.label || placeholder}
        </span>
        <svg className="itc-dd-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <div className="itc-dd-menu">
          {showSearch && (
            <div className="itc-dd-search">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                value={query}
                placeholder="Search environments..."
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActiveIndex(0);
                }}
              />
            </div>
          )}
          <ul className="itc-dd-list" role="listbox" ref={listRef}>
            {filtered.length === 0 ? (
              <li className="itc-dd-empty">No matching environments</li>
            ) : (
              filtered.map((option, index) => {
                const isSelected = option.value === value;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    title={option.label}
                    className={`itc-dd-option ${isSelected ? "is-selected" : ""} ${
                      index === activeIndex ? "is-active" : ""
                    }`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => choose(option)}
                  >
                    <span className="itc-dd-option-label">{option.label}</span>
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

/**
 * Modal for importing manual test cases from an Excel sheet.
 * Calls POST /test_case/import_manual_test_cases?project_id=&environment_id=
 * with the file as multipart/form-data (token header is added by axiosInstance).
 * The project comes from the navbar project context.
 */
const ImportTestCasesModal = ({
  isOpen,
  onClose,
  onImported,
  projectId,
  projectName = "",
}) => {
  const [environmentId, setEnvironmentId] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef(null);

  const fetchEnvironments = useCallback(async (pid) => {
    if (!pid) {
      setEnvironments([]);
      return;
    }
    try {
      setEnvironmentsLoading(true);
      const response = await axiosInstance.get(
        `/user_profile/list_environments?project_id=${pid}`,
        { skipGlobalLoader: true }
      );
      const list = response?.status_code === 200 ? response?.data || [] : [];
      setEnvironments(list);
      if (list.length === 1) setEnvironmentId(String(list[0].id));
    } catch (error) {
      console.error("Error fetching environments:", error);
      setEnvironments([]);
    } finally {
      setEnvironmentsLoading(false);
    }
  }, []);

  // Reset state and load environments each time the modal opens
  useEffect(() => {
    if (!isOpen) return;
    setEnvironmentId("");
    setFile(null);
    setErrorMessage("");
    setIsDragging(false);
    fetchEnvironments(projectId);
  }, [isOpen, projectId, fetchEnvironments]);

  // Close on Escape (unless an import is in progress)
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKey = (e) => {
      if (e.key === "Escape" && !isImporting) onClose?.();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, isImporting, onClose]);

  const selectFile = (selected) => {
    if (!selected) return;
    if (!hasAcceptedExtension(selected.name)) {
      setErrorMessage(
        "Only Excel files (.xlsx, .xls) are supported. Please use the template."
      );
      return;
    }
    setErrorMessage("");
    setFile(selected);
  };

  const handleFileInputChange = (e) => {
    selectFile(e.target.files?.[0]);
    // allow re-selecting the same file after removal
    e.target.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isImporting) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (isImporting) return;
    selectFile(e.dataTransfer?.files?.[0]);
  };

  const openFilePicker = () => {
    if (!isImporting) fileInputRef.current?.click();
  };

  const handleClose = () => {
    if (!isImporting) onClose?.();
  };

  const canSubmit = Boolean(projectId && environmentId && file) && !isImporting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setIsImporting(true);
      setErrorMessage("");
      const response = await axiosInstance.post(
        `/test_case/import_manual_test_cases`,
        formData,
        {
          params: {
            project_id: Number(projectId),
            environment_id: Number(environmentId),
          },
          skipGlobalToast: true,
        }
      );

      const statusCode = Number(response?.status_code);
      const status = String(response?.status || "").toLowerCase();
      const isFailure =
        (statusCode && statusCode >= 400) ||
        status === "error" ||
        status === "failed";

      if (isFailure) {
        setErrorMessage(response?.message || "Import failed. Please try again.");
        return;
      }

      onImported?.(Number(projectId), response);
    } catch (error) {
      console.error("Error importing test cases:", error);
      setErrorMessage(extractErrorMessage(error));
    } finally {
      setIsImporting(false);
    }
  };

  if (!isOpen) return null;

  const environmentOptions = environments.map((env) => ({
    value: String(env.id),
    label: env.environment_name,
  }));

  const environmentPlaceholder = environmentsLoading
    ? "Loading environments..."
    : environments.length === 0
    ? "No environments found"
    : "Select an environment";

  return createPortal(
    <div className="itc-overlay" onMouseDown={handleClose}>
      <div
        className="itc-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="itc-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="itc-header">
          <div className="itc-header-icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="itc-header-text">
            <h2 id="itc-title">Import Test Cases</h2>
            <p>
              Bulk import manual test cases
              {projectName ? (
                <>
                  {" into "}
                  <strong title={projectName}>{projectName}</strong>
                </>
              ) : (
                " from an Excel sheet"
              )}
            </p>
          </div>
          <button
            type="button"
            className="itc-close"
            onClick={handleClose}
            disabled={isImporting}
            aria-label="Close"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="itc-form">
          <div className="itc-body">
            {/* Template download */}
            <div className="itc-template">
              <div className="itc-template-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <path d="M8 13h8M8 17h8M10 9H8" />
                </svg>
              </div>
              <div className="itc-template-text">
                <span className="itc-template-title">Need the format?</span>
                <span className="itc-template-sub">
                  Download the Excel template, fill in your test cases, then upload it.
                </span>
              </div>
              <a
                className="itc-template-btn"
                href={TEMPLATE_URL}
                download="import_template.xlsx"
                target="_blank"
                rel="noopener noreferrer"
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Template
              </a>
            </div>

            {/* Environment */}
            <div className="itc-field">
              <label htmlFor="itc-env">
                Environment <span className="itc-required">*</span>
              </label>
              <EnvironmentSelect
                value={environmentId}
                onChange={setEnvironmentId}
                options={environmentOptions}
                placeholder={environmentPlaceholder}
                disabled={
                  environmentsLoading || isImporting || environments.length === 0
                }
              />
            </div>

            {/* Upload */}
            <div className="itc-field">
              <label>
                Excel file <span className="itc-required">*</span>
              </label>

              {!file ? (
                <div
                  className={`itc-dropzone ${isDragging ? "is-dragging" : ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={openFilePicker}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openFilePicker();
                    }
                  }}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <div className="itc-dropzone-icon" aria-hidden="true">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 14.9A7 7 0 1 1 15.7 8h1.8a4.5 4.5 0 0 1 2.5 8.2" />
                      <path d="M12 12v9" />
                      <path d="m16 16-4-4-4 4" />
                    </svg>
                  </div>
                  <p className="itc-dropzone-title">
                    <span>Click to upload</span> or drag and drop
                  </p>
                  <p className="itc-dropzone-hint">
                    Excel files only (.xlsx, .xls)
                  </p>
                </div>
              ) : (
                <div className="itc-file-card">
                  <div className="itc-file-icon" aria-hidden="true">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <path d="M8 13h8M8 17h8M10 9H8" />
                    </svg>
                  </div>
                  <div className="itc-file-meta">
                    <span className="itc-file-name" title={file.name}>
                      {file.name}
                    </span>
                    <span className="itc-file-size">
                      {formatFileSize(file.size)}
                      {isImporting ? " · Uploading..." : " · Ready to import"}
                    </span>
                    {isImporting && (
                      <div className="itc-progress">
                        <div className="itc-progress-bar" />
                      </div>
                    )}
                  </div>
                  {!isImporting && (
                    <div className="itc-file-actions">
                      <button type="button" className="itc-link-btn" onClick={openFilePicker}>
                        Replace
                      </button>
                      <button
                        type="button"
                        className="itc-icon-btn"
                        onClick={() => setFile(null)}
                        aria-label="Remove file"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                          <path d="M10 11v6M14 11v6" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPT_ATTR}
                onChange={handleFileInputChange}
                hidden
              />
            </div>

            {errorMessage && (
              <div className="itc-alert" role="alert">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span>{errorMessage}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="itc-footer">
            <button
              type="button"
              className="itc-btn itc-btn-secondary"
              onClick={handleClose}
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="itc-btn itc-btn-primary"
              disabled={!canSubmit}
            >
              {isImporting ? (
                <>
                  <span className="itc-spinner" aria-hidden="true" />
                  Importing...
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  Import Test Cases
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ImportTestCasesModal;
